import React, { useState, useEffect } from 'react';
import { API } from '../services/api';
import './PublicTemplatePickerModal.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function PublicTemplatePickerModal({ isOpen, onClose, onSelectTemplate, onStartEmpty }) {
    const [templates, setTemplates] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen) {
            loadTemplates();
        }
    }, [isOpen]);

    const loadTemplates = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await API.getPublicTemplates();
            if (response?.data) {
                setTemplates(response.data.templates || []);
                setCategories(response.data.categories || []);
            }
        } catch (err) {
            console.error('Failed to load public templates:', err);
            setError('Impossible de charger les modèles');
        } finally {
            setLoading(false);
        }
    };

    const handleSelectTemplate = async () => {
        if (!selectedTemplate) return;

        try {
            const response = await API.applyPublicTemplate(selectedTemplate.id);
            if (response?.data?.template_data) {
                onSelectTemplate(response.data.template_data);
            }
        } catch (err) {
            console.error('Failed to use template:', err);
            setError('Erreur lors du chargement du modèle');
        }
    };

    const filteredTemplates = selectedCategory
        ? templates.filter(t => t.category_slug === selectedCategory)
        : templates;

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="public-template-picker-modal" onClick={e => e.stopPropagation()}>
                <div className="picker-header">
                    <h2>Choisir un modèle</h2>
                    <button className="close-btn" onClick={onClose}>&times;</button>
                </div>

                {/* Onglets catégories */}
                <div className="picker-tabs">
                    <button
                        className={`tab-btn ${selectedCategory === null ? 'active' : ''}`}
                        onClick={() => setSelectedCategory(null)}
                    >
                        Tous
                    </button>
                    {categories.map(cat => (
                        <button
                            key={cat.id}
                            className={`tab-btn ${selectedCategory === cat.slug ? 'active' : ''}`}
                            onClick={() => setSelectedCategory(cat.slug)}
                        >
                            {cat.name}
                        </button>
                    ))}
                </div>

                {/* Contenu */}
                <div className="picker-content">
                    {loading ? (
                        <div className="picker-loading">
                            <div className="spinner"></div>
                            <p>Chargement des modèles...</p>
                        </div>
                    ) : error ? (
                        <div className="picker-error">
                            <p>{error}</p>
                            <button className="btn btn-secondary" onClick={loadTemplates}>
                                Réessayer
                            </button>
                        </div>
                    ) : filteredTemplates.length === 0 ? (
                        <div className="picker-empty">
                            <p>Aucun modèle disponible dans cette catégorie</p>
                        </div>
                    ) : (
                        <div className="templates-grid">
                            {filteredTemplates.map(template => (
                                <div
                                    key={template.id}
                                    className={`template-card ${selectedTemplate?.id === template.id ? 'selected' : ''}`}
                                    onClick={() => setSelectedTemplate(selectedTemplate?.id === template.id ? null : template)}
                                >
                                    <div className="template-thumbnail">
                                        {template.thumbnail_url ? (
                                            <img src={`${API_URL}${template.thumbnail_url}`} alt={template.name} />
                                        ) : (
                                            <div className="thumbnail-placeholder">
                                                <span>Aperçu</span>
                                            </div>
                                        )}
                                        {template.is_featured === 1 && (
                                            <span className="featured-badge">Populaire</span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="picker-footer">
                    <button className="btn btn-secondary" onClick={onStartEmpty}>
                        Partir de zéro
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={handleSelectTemplate}
                        disabled={!selectedTemplate}
                    >
                        Utiliser ce modèle
                    </button>
                </div>
            </div>
        </div>
    );
}

export default PublicTemplatePickerModal;
